/**
 * volatilityInterpreter.ts — MER Algo Footprints v8.2.0
 *
 * Pure deterministic volatility context interpreter.
 *
 * Classifies a VIX level (or equivalent volatility proxy) into a named regime
 * and produces deterministic behavior profile modifiers.
 *
 * ── Design principles ─────────────────────────────────────────────────────────
 *
 * 1. Structural neutrality — VIX does NOT alter gamma, wall, or flip-zone math.
 *    It alters interpretation of those outputs and confidence in them.
 *
 * 2. Deterministic — same VIX level always produces the same regime/modifiers.
 *
 * 3. Additive — downstream consumers apply modifiers optionally.
 *    Nothing breaks if volContext is null.
 *
 * 4. Replay-safe — pure function, no I/O, no side effects.
 *
 * ── Integration targets ───────────────────────────────────────────────────────
 *
 * 1. Thesis engine (promptBuilder.ts)
 *    → adds volatility regime line to the AI prompt context
 *    → modifiers inform how aggressively the AI should temper breakout/reversal calls
 *
 * 2. Trap engine (trapInterpreter.ts)
 *    → trapRisk multiplied by trapRiskModifier
 *    → whipsawRisk elevated under high/extreme vol
 *
 * 3. Gamma + Liquidity interpretation (gammaContextMapper.ts)
 *    → compressionStable downgraded under extreme vol
 *    → breakout reliability reduced under high vol
 *
 * ── Modifier semantics ────────────────────────────────────────────────────────
 *
 * Modifiers are multiplicative scalars (0.5–1.5 range) applied to interpretation
 * quality scores. A modifier of 1.0 = no change. > 1.0 = amplified. < 1.0 = dampened.
 *
 *   confidenceModifier         — multiply final confidence by this
 *   breakoutReliabilityModifier— multiply breakout validity score by this
 *   reversalReliabilityModifier— multiply reversal quality score by this
 *   trapRiskModifier           — multiply trap risk score by this
 *
 * ── Regime table ──────────────────────────────────────────────────────────────
 *
 * Regime      VIX range   confMod  breakoutMod  reversalMod  trapMod
 * low_vol     < 15        1.05     1.10         1.05         0.85
 * normal_vol  15–24       1.00     1.00         1.00         1.00
 * high_vol    25–34       0.90     0.85         0.85         1.20
 * extreme_vol ≥ 35        0.75     0.70         0.70         1.40
 *
 * Behavior profile (used for reasoning / interpretive context):
 *   expansionBias       — how much to expect directional expansion vs containment
 *   trapProbability     — baseline probability multiplier for false-move scenarios
 *   whipsawRisk         — likelihood of sharp reversals / direction instability
 *   compressionFragility— how stable apparent compression/pinning is likely to be
 *
 * ── Replay compatibility ──────────────────────────────────────────────────────
 *
 * classifyVolatility(vixLevel) is a pure function.
 * Replay requires only the saved vixLevel — all outputs are deterministically derived.
 */

// ── Centralized threshold configuration ──────────────────────────────────────

export const VOLATILITY_CONTEXT_THRESHOLDS = {
  // VIX regime cutoffs (upper bound of each regime band)
  LOW_VOL_MAX:    15,   // VIX < 15 = low_vol
  NORMAL_VOL_MAX: 25,   // 15 ≤ VIX < 25 = normal_vol
  HIGH_VOL_MAX:   35,   // 25 ≤ VIX < 35 = high_vol
                        // VIX ≥ 35 = extreme_vol

  // ── Bounded scaling weight ─────────────────────────────────────────────────
  // Modifiers are applied via bounded lerp, not raw multiplication:
  //   adjusted = base + (base * (modifier - 1) * MODIFIER_WEIGHT)
  // This ensures volatility acts as a bias (max ~20-30% shift) not a distortion.
  // Weight 0.40 means extreme_vol (modifier 1.40) shifts a 0.50 score to 0.58,
  // not to 0.70 as raw multiplication would produce.
  MODIFIER_WEIGHT: 0.40,

  // ── Output caps ────────────────────────────────────────────────────────────
  // Prevent runaway values regardless of input or modifier combination.
  CAP_TRAP_RISK_MAX:        0.95,  // trap score never clamped above this by vol
  CAP_WHIPSAW_MAX:          0.95,
  CAP_CONFIDENCE_MIN:       0.08,  // confidence floor after all vol penalties
  CAP_CONFIDENCE_MAX:       1.00,
  CAP_REGIME_CONF_MIN:      0.05,  // regimeConfidence floor after vol penalty

  // ── Confidence modifiers per regime ────────────────────────────────────────
  // Used as raw penalty multiplier applied AFTER base confidence calculation.
  // Distinct from bounded lerp (confidence uses direct multiply, not lerp).
  CONF_MOD_LOW_VOL:     1.05,
  CONF_MOD_NORMAL_VOL:  1.00,
  CONF_MOD_HIGH_VOL:    0.90,
  CONF_MOD_EXTREME_VOL: 0.80,   // was 0.75 — less aggressive with cleaner model

  // ── Breakout reliability modifiers (applied via bounded lerp) ─────────────
  BREAKOUT_MOD_LOW_VOL:     1.10,
  BREAKOUT_MOD_NORMAL_VOL:  1.00,
  BREAKOUT_MOD_HIGH_VOL:    0.85,
  BREAKOUT_MOD_EXTREME_VOL: 0.70,

  // ── Reversal reliability modifiers (applied via bounded lerp) ─────────────
  REVERSAL_MOD_LOW_VOL:     1.05,
  REVERSAL_MOD_NORMAL_VOL:  1.00,
  REVERSAL_MOD_HIGH_VOL:    0.85,
  REVERSAL_MOD_EXTREME_VOL: 0.70,

  // ── Trap risk modifiers (applied via bounded lerp) ─────────────────────────
  TRAP_MOD_LOW_VOL:     0.85,
  TRAP_MOD_NORMAL_VOL:  1.00,
  TRAP_MOD_HIGH_VOL:    1.20,
  TRAP_MOD_EXTREME_VOL: 1.40,

  // ── Flip-zone + volatility dominance trigger ───────────────────────────────
  // When flip proximity score meets this threshold AND vol is high/extreme,
  // flip_zone_trap classification is forced regardless of other scores.
  FLIP_VOL_DOMINANCE_SCORE: 0.45,  // lower than HIGH_TRAP_RISK_MIN (0.65) — vol amplifies

  // Behavior profile values (used for reasoning/display, not directly as multipliers)
  // expansionBias: relative probability of directional expansion
  EXPANSION_BIAS_LOW:     0.85,
  EXPANSION_BIAS_NORMAL:  1.00,
  EXPANSION_BIAS_HIGH:    1.15,
  EXPANSION_BIAS_EXTREME: 1.30,

  // trapProbability, whipsawRisk, compressionFragility follow same pattern
  TRAP_PROB_LOW:     0.85,
  TRAP_PROB_NORMAL:  1.00,
  TRAP_PROB_HIGH:    1.20,
  TRAP_PROB_EXTREME: 1.35,

  WHIPSAW_LOW:     0.80,
  WHIPSAW_NORMAL:  1.00,
  WHIPSAW_HIGH:    1.20,
  WHIPSAW_EXTREME: 1.40,

  COMPRESSION_FRAGILITY_LOW:     0.80,
  COMPRESSION_FRAGILITY_NORMAL:  1.00,
  COMPRESSION_FRAGILITY_HIGH:    1.15,
  COMPRESSION_FRAGILITY_EXTREME: 1.30,
} as const;

const T = VOLATILITY_CONTEXT_THRESHOLDS;

// ── Types ─────────────────────────────────────────────────────────────────────

export type VolatilityRegime = 'low_vol' | 'normal_vol' | 'high_vol' | 'extreme_vol';

export interface VolatilityBehaviorProfile {
  /** Relative likelihood of sustained directional moves (1.0 = baseline) */
  expansionBias:         number;
  /** Multiplier on baseline trap probability */
  trapProbability:       number;
  /** Likelihood of sharp direction reversals */
  whipsawRisk:           number;
  /** How fragile apparent pinning/compression is */
  compressionFragility:  number;
}

export interface VolatilityModifiers {
  /** Multiply final interpretation confidence by this */
  confidenceModifier:           number;
  /** Multiply breakout quality/validity scores by this */
  breakoutReliabilityModifier:  number;
  /** Multiply reversal quality scores by this */
  reversalReliabilityModifier:  number;
  /** Multiply trap risk scores by this */
  trapRiskModifier:             number;
}

export type VolatilityImpact = 'low_risk' | 'balanced' | 'elevated_risk' | 'high_instability';

export function regimeToImpact(regime: VolatilityRegime): VolatilityImpact {
  switch (regime) {
    case 'low_vol':     return 'low_risk';
    case 'high_vol':    return 'elevated_risk';
    case 'extreme_vol': return 'high_instability';
    default:            return 'balanced';
  }
}

export interface VolatilityContext {
  symbol:    string;   // e.g. 'VIX'
  asOf:      string;   // ISO timestamp

  vixLevel:  number | null;

  regime:    VolatilityRegime;
  /** Human-readable impact classification: low_risk | balanced | elevated_risk | high_instability */
  impact:    VolatilityImpact;

  behaviorProfile: VolatilityBehaviorProfile;
  modifiers:       VolatilityModifiers;

  /**
   * Stable reasoning order:
   *   1. current volatility regime and VIX level
   *   2. resulting behavior profile implication
   *   3. effect on interpretation / confidence
   */
  reasoning: string[];

  meta: {
    provider:          string;
    requestedProvider: string;
    fallbackActive:    boolean;
    thresholdsUsed:    Record<string, number>;
    isStale?:          boolean;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _round2(v: number): number { return Math.round(v * 100) / 100; }

/**
 * Bounded lerp modifier application.
 * Formula: adjusted = base + (base * (modifier - 1) * weight)
 * Equivalent to: lerp(base, base * modifier, weight)
 *
 * At weight=0.40 and modifier=1.40: shift is 40% × 0.40 = 16% of base.
 * At weight=0.40 and modifier=0.75: shift is -25% × 0.40 = -10% of base.
 * This prevents single-modifier distortion while preserving directional effect.
 */
function _applyBoundedMod(base: number, modifier: number): number {
  const weight = T.MODIFIER_WEIGHT;
  return _round2(base + base * (modifier - 1) * weight);
}

/**
 * Named volatility penalty — applied directly to confidence (not bounded lerp).
 * Uses simpler direct multiply because confidence is already in [0,1] and
 * the penalty must be clearly auditable and predictable.
 *
 * Returns the confidence multiplier for the given regime.
 */
export function volatilityPenalty(regime: VolatilityRegime): number {
  switch (regime) {
    case 'low_vol':     return T.CONF_MOD_LOW_VOL;
    case 'high_vol':    return T.CONF_MOD_HIGH_VOL;
    case 'extreme_vol': return T.CONF_MOD_EXTREME_VOL;
    default:            return T.CONF_MOD_NORMAL_VOL;
  }
}

// ── Regime classification ─────────────────────────────────────────────────────

export function classifyVolatilityRegime(vixLevel: number): VolatilityRegime {
  if (vixLevel < T.LOW_VOL_MAX)    return 'low_vol';
  if (vixLevel < T.NORMAL_VOL_MAX) return 'normal_vol';
  if (vixLevel < T.HIGH_VOL_MAX)   return 'high_vol';
  return 'extreme_vol';
}

// ── Behavior profile derivation ───────────────────────────────────────────────

function _behaviorProfile(regime: VolatilityRegime): VolatilityBehaviorProfile {
  switch (regime) {
    case 'low_vol': return {
      expansionBias:        T.EXPANSION_BIAS_LOW,
      trapProbability:      T.TRAP_PROB_LOW,
      whipsawRisk:          T.WHIPSAW_LOW,
      compressionFragility: T.COMPRESSION_FRAGILITY_LOW,
    };
    case 'high_vol': return {
      expansionBias:        T.EXPANSION_BIAS_HIGH,
      trapProbability:      T.TRAP_PROB_HIGH,
      whipsawRisk:          T.WHIPSAW_HIGH,
      compressionFragility: T.COMPRESSION_FRAGILITY_HIGH,
    };
    case 'extreme_vol': return {
      expansionBias:        T.EXPANSION_BIAS_EXTREME,
      trapProbability:      T.TRAP_PROB_EXTREME,
      whipsawRisk:          T.WHIPSAW_EXTREME,
      compressionFragility: T.COMPRESSION_FRAGILITY_EXTREME,
    };
    default: return {
      expansionBias:        T.EXPANSION_BIAS_NORMAL,
      trapProbability:      T.TRAP_PROB_NORMAL,
      whipsawRisk:          T.WHIPSAW_NORMAL,
      compressionFragility: T.COMPRESSION_FRAGILITY_NORMAL,
    };
  }
}

// ── Modifier derivation ───────────────────────────────────────────────────────

function _modifiers(regime: VolatilityRegime): VolatilityModifiers {
  switch (regime) {
    case 'low_vol': return {
      confidenceModifier:          T.CONF_MOD_LOW_VOL,
      breakoutReliabilityModifier: T.BREAKOUT_MOD_LOW_VOL,
      reversalReliabilityModifier: T.REVERSAL_MOD_LOW_VOL,
      trapRiskModifier:            T.TRAP_MOD_LOW_VOL,
    };
    case 'high_vol': return {
      confidenceModifier:          T.CONF_MOD_HIGH_VOL,
      breakoutReliabilityModifier: T.BREAKOUT_MOD_HIGH_VOL,
      reversalReliabilityModifier: T.REVERSAL_MOD_HIGH_VOL,
      trapRiskModifier:            T.TRAP_MOD_HIGH_VOL,
    };
    case 'extreme_vol': return {
      confidenceModifier:          T.CONF_MOD_EXTREME_VOL,
      breakoutReliabilityModifier: T.BREAKOUT_MOD_EXTREME_VOL,
      reversalReliabilityModifier: T.REVERSAL_MOD_EXTREME_VOL,
      trapRiskModifier:            T.TRAP_MOD_EXTREME_VOL,
    };
    default: return {
      confidenceModifier:          T.CONF_MOD_NORMAL_VOL,
      breakoutReliabilityModifier: T.BREAKOUT_MOD_NORMAL_VOL,
      reversalReliabilityModifier: T.REVERSAL_MOD_NORMAL_VOL,
      trapRiskModifier:            T.TRAP_MOD_NORMAL_VOL,
    };
  }
}

// ── Reasoning builder ─────────────────────────────────────────────────────────

function _buildReasoning(
  vixLevel: number | null,
  regime:   VolatilityRegime,
  profile:  VolatilityBehaviorProfile,
  mods:     VolatilityModifiers,
): string[] {
  const r: string[] = [];
  const vixStr = vixLevel !== null ? `VIX ${vixLevel.toFixed(2)}` : 'VIX unavailable';

  // Section 1: regime and VIX level
  const regimeLabel = {
    low_vol:     'Low volatility',
    normal_vol:  'Normal volatility',
    high_vol:    'Elevated volatility',
    extreme_vol: 'Extreme volatility / fear spike',
  }[regime];
  r.push(`${regimeLabel} regime (${vixStr})`);

  // Section 2: behavior profile implications
  if (regime === 'low_vol') {
    r.push('Low-vol environment: pinning / compression more reliable; expansion moves typically contained');
    r.push('Trap probability and whipsaw risk are reduced; structural signals carry more weight');
  } else if (regime === 'normal_vol') {
    r.push('Normal-vol environment: baseline behavior for all structural signals');
  } else if (regime === 'high_vol') {
    r.push(`Elevated-vol environment: expansion bias +${((profile.expansionBias - 1) * 100).toFixed(0)}%, whipsaw risk +${((profile.whipsawRisk - 1) * 100).toFixed(0)}%`);
    r.push('Compression and pinning are less reliable; breakouts may overshoot then reverse');
  } else {
    r.push(`Extreme-vol environment: high instability; structural signals should be treated with significant caution`);
    r.push(`Trap probability +${((profile.trapProbability - 1) * 100).toFixed(0)}%, whipsaw risk +${((profile.whipsawRisk - 1) * 100).toFixed(0)}%, compression fragility +${((profile.compressionFragility - 1) * 100).toFixed(0)}%`);
  }

  // Section 3: effect on interpretation and confidence
  if (mods.confidenceModifier < 1.0) {
    r.push(`Confidence modifier: ×${mods.confidenceModifier} — structural signal confidence reduced in this vol environment`);
  } else if (mods.confidenceModifier > 1.0) {
    r.push(`Confidence modifier: ×${mods.confidenceModifier} — structural signals are more reliable in low-vol conditions`);
  }
  if (mods.trapRiskModifier > 1.0) {
    r.push(`Trap risk modifier: ×${mods.trapRiskModifier} — elevated probability of false moves and traps`);
  }

  return r;
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Produce a VolatilityContext from a VIX level (or null when unavailable).
 *
 * When vixLevel is null, returns a 'normal_vol' context with a neutral behavior
 * profile and 1.0 modifiers — no interpretation bias is applied.
 *
 * @param vixLevel — current VIX index level (e.g. 18.4)
 * @param opts     — optional metadata
 */
export function classifyVolatility(
  vixLevel: number | null,
  opts: {
    symbol?:          string;
    provider?:        string;
    requestedProvider?: string;
    fallbackActive?:  boolean;
    isStale?:         boolean;
  } = {},
): VolatilityContext {
  const symbol   = opts.symbol   ?? 'VIX';
  const provider = opts.provider ?? 'unknown';

  // When VIX is unavailable: use normal_vol (neutral, no interpretation bias)
  const regime  = vixLevel !== null ? classifyVolatilityRegime(vixLevel) : 'normal_vol';
  const profile = _behaviorProfile(regime);
  const mods    = _modifiers(regime);
  const reasoning = _buildReasoning(vixLevel, regime, profile, mods);

  if (vixLevel === null) {
    reasoning.unshift('VIX data unavailable — neutral volatility context applied (no interpretation bias)');
  }

  return {
    symbol,
    asOf:            new Date().toISOString(),
    vixLevel,
    regime,
    impact:          regimeToImpact(regime),
    behaviorProfile: {
      expansionBias:        _round2(profile.expansionBias),
      trapProbability:      _round2(profile.trapProbability),
      whipsawRisk:          _round2(profile.whipsawRisk),
      compressionFragility: _round2(profile.compressionFragility),
    },
    modifiers: {
      confidenceModifier:          _round2(mods.confidenceModifier),
      breakoutReliabilityModifier: _round2(mods.breakoutReliabilityModifier),
      reversalReliabilityModifier: _round2(mods.reversalReliabilityModifier),
      trapRiskModifier:            _round2(mods.trapRiskModifier),
    },
    reasoning,
    meta: {
      provider,
      requestedProvider: opts.requestedProvider ?? provider,
      fallbackActive:    opts.fallbackActive ?? false,
      isStale:           opts.isStale ?? false,
      thresholdsUsed: {
        LOW_VOL_MAX:    T.LOW_VOL_MAX,
        NORMAL_VOL_MAX: T.NORMAL_VOL_MAX,
        HIGH_VOL_MAX:   T.HIGH_VOL_MAX,
      },
    },
  };
}

/**
 * Apply volatility confidence penalty.
 *
 * Uses direct multiply (not bounded lerp) so the reduction is clearly auditable:
 *   adjusted = confidence * volatilityPenalty(regime)
 *
 * Result is clamped to [CAP_CONFIDENCE_MIN, CAP_CONFIDENCE_MAX].
 * If volContext is null, confidence is unchanged.
 */
export function applyVolConfidenceModifier(
  confidence: number,
  volContext: VolatilityContext | null | undefined,
): number {
  if (!volContext) return confidence;
  const penalty = volatilityPenalty(volContext.regime);
  const adjusted = confidence * penalty;
  return _round2(Math.max(T.CAP_CONFIDENCE_MIN, Math.min(T.CAP_CONFIDENCE_MAX, adjusted)));
}

/**
 * Apply volatility modifier to a trap risk score via bounded lerp.
 *
 * Formula: adjusted = base + (base * (modifier - 1) * MODIFIER_WEIGHT)
 * This prevents extreme_vol from linearly inflating trap scores by 40%.
 * Instead the shift is modifier_delta × 0.40 — a proportional bias, not a distortion.
 *
 * Result clamped to [0, CAP_TRAP_RISK_MAX].
 * If volContext is null, score is unchanged.
 */
export function applyVolTrapModifier(
  trapScore: number,
  volContext: VolatilityContext | null | undefined,
): number {
  if (!volContext) return trapScore;
  const mod = volContext.modifiers.trapRiskModifier;
  const adjusted = _applyBoundedMod(trapScore, mod);
  return _round2(Math.max(0, Math.min(T.CAP_TRAP_RISK_MAX, adjusted)));
}

/**
 * Apply volatility modifier to regimeConfidence.
 *
 * High/extreme volatility increases regime instability and reduces confidence
 * in the classified gamma regime. Applied as the same penalty as confidence.
 *
 * Result clamped to [CAP_REGIME_CONF_MIN, 1.0].
 */
export function applyVolRegimeConfModifier(
  regimeConfidence: number,
  volContext: VolatilityContext | null | undefined,
): number {
  if (!volContext) return regimeConfidence;
  const penalty = volatilityPenalty(volContext.regime);
  const adjusted = regimeConfidence * penalty;
  return _round2(Math.max(T.CAP_REGIME_CONF_MIN, Math.min(1.0, adjusted)));
}

/**
 * Apply volatility modifier to breakout or reversal reliability via bounded lerp.
 * If volContext is null, score is unchanged.
 */
export function applyVolBreakoutModifier(
  score: number,
  volContext: VolatilityContext | null | undefined,
): number {
  if (!volContext) return score;
  const mod = volContext.modifiers.breakoutReliabilityModifier;
  return _round2(Math.max(0, Math.min(1, _applyBoundedMod(score, mod))));
}
